import { getPlatforms, IonButton, IonButtons, IonCard, IonIcon, IonInput, IonItem, IonLabel, IonRow, IonToast } from "@ionic/react";
import { eyeOffOutline, eyeOutline } from "ionicons/icons";
import { FC, SetStateAction, useEffect, useRef, useState } from "react";
import { SavePassword } from "capacitor-ios-autofill-save-password";
import { defaultMqttBroker, LoadPreference, SavePreference }            from "components/AppSettings";
import { DropdownSelectItem } from "components/DropdownSelect";
import { Page }               from "components/Page";
import { Glob, useLoc } from "functions/Language";
import { CONNECT_TIMEOUT, LoginFailureModes } from "functions/networking/Networking";
import { useAsyncEffect, useMounted } from "functions/Util";
import { Profile, ParseUsers, ProfileSettings, StringifyUsers, ProfileDisplay } from "Login/Profiles";

import "Login/login.css";

const PROF_NEW = -1;
const PROF_TMP = -2;

export const LoginPage : FC<{
	login: (profile: Profile) => Promise<boolean>,
	mqttErr: LoginFailureModes | null,
}> = (props) => {
	const loc = useLoc(glob);
	const mounted = useMounted();
	const [externalSite, setExternalSite] = useState("");

	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [locError, setLocError] = useState(props.mqttErr);
	useEffect(function RaiseError() { if (props.mqttErr !== null) setLocError(props.mqttErr); }, [props.mqttErr]);

	const [users, setUsers] = useState<Profile[]>([]);
	const [selUser, setSelUser] = useState<Profile>({ name: "", pass: "", broker: defaultMqttBroker });

	const [pid, setPid] = useState(0);
	const pid_last = useRef(pid);

	const [showPwd, setShowPwd] = useState(false);
	useEffect(function LoadProfile() { setShowPwd(false); }, [selUser]);

	const [loginWait, setLoginWait] = useState(false);


	useEffect(function CopyProfile() {
		let profile = users[pid] as Profile | undefined;
		if (profile) {
			setSelUser({ ... profile });
		} else if (pid_last.current >= 0) {
			setSelUser(old => ({
				broker: old.broker ?? defaultMqttBroker,
				name: "",
				pass: "",
			}));
		}
		// else prepiname mezi new/tmp - nemazat

		pid_last.current = pid;
	}, [pid, users]);

	async function LoadProfiles(abort?: AbortSignal) {
		if (abort?.aborted) return;

		let l_users = await LoadPreference("users");
		let l_sel = await LoadPreference("selUser");

		if (abort?.aborted) return;

		let l_profiles: typeof users = [];
		if (l_users !== null) {
			let parse = await ParseUsers(l_users);
			if (abort?.aborted) return;
			if (parse.err) return;

			l_profiles = parse.users;
		}

		let l_pid = l_profiles.length ? 0 : PROF_NEW;
		if (l_sel !== null) {
			let profile = FindProfileByName(l_profiles, l_sel);
			if (profile > 0) l_pid = profile;
		}

		setPid(l_pid);
		setUsers(l_profiles);
	}

	useAsyncEffect(LoadProfiles, []);

	useEffect(function PresentError() {
		switch (props.mqttErr) {
		case LoginFailureModes.credentials:
			setErrorMessage(loc.loginFailed);
			break;
		case LoginFailureModes.connection:
			setErrorMessage(loc.connectionFailed);
			break;
		default:
			break;
		}
	}, [props.mqttErr]);

	async function UpdateProfiles(new_profiles: SetStateAction<Profile[]>) {
		if (typeof new_profiles === "function") new_profiles = new_profiles(users);

		let new_sel : Profile |  null = selUser;
		let new_pid = new_profiles.indexOf(selUser);

		if (new_pid < 0) {
			if (new_profiles.length) new_pid = 0;
			else                     new_pid = PROF_NEW;

			new_sel = new_profiles[new_pid] ?? null;
		}

		await SaveProfiles(new_profiles, new_pid, new_sel, /*destructive=*/true);

		await LoadProfiles();
	}

  return <Page title={loc.title} hideBackButton>
    <div style={{ height: "100%", marginBottom: "0px", marginTop: "0px" }}>
    <IonCard style={{maxWidth: "40em", margin: "auto", minHeight: "100%"}}>
    <div
      style={{
        position: "absolute",
        height: "100%",
        width: "100%",
        zIndex: 1000,
        backgroundColor: "var(--background)",
        overflow: "hidden",
        display: externalSite.length ? "flex" : "none",
        flexDirection: "column"
      }}>
        <IonRow>
          <IonButton style={{flex: 1}} onClick={()=>setExternalSite("")} expand="block">{loc.returnToLogin}</IonButton>
          <IonButton style={{flex: 1}} href={externalSite} expand="block" onClick={()=>setExternalSite("")} target="_blank">{loc.openInBrowser}</IonButton>
        </IonRow>
      <iframe src={externalSite} style={{flexGrow: 1, border: "none", margin: 0, padding: 0}}></iframe>
    </div>

		<form onSubmit={async (e) => {
			e.preventDefault();

			if (mounted.current) setLoginWait(true);
			let timeout = setTimeout(() => {
				if (mounted.current) setLoginWait(false);
			}, CONNECT_TIMEOUT);

			if (await props.login({... selUser})) {
				// successful login
				SaveProfiles(users, pid, selUser, /*destructive=*/true);
			}
			clearTimeout(timeout);
			if (mounted.current) setLoginWait(false);

		}}>
			<span style={{ display: "flex" }}>
				<DropdownSelectItem
					label={loc.selUser}
					selected={pid}
					setSelected={setPid}
					options={users.map((u, i) => <ProfileDisplay key={i} profile={u} otherProfiles={users} />)}
					specialOptions={[
						{ key: PROF_NEW, value: loc.profile_new },
						{ key: PROF_TMP, value: loc.profile_tmp },
					]}
					lines="none"
				/>

				<IonButtons slot="end" style={{ alignSelf: "flex-end", marginBlock: "auto", marginInline: "0px" }}>
					<ProfileSettings profiles={users} updateProfiles={UpdateProfiles} selectedProfile={selUser} />
				</IonButtons>
			</span>

			<div style={locError === LoginFailureModes.credentials ? { borderLeft: "solid red 2px" } : undefined}>
				{/* username */}
				<IonInput
					mode="md"
					labelPlacement="stacked"
					className="inputlogin"
					type="text"
					fill="outline"
					required
					value={selUser.name}
					onIonInput={e => {
						selUser.name = (e.detail.value ?? "").toLowerCase();
						setLocError(null);
						setLoginWait(false);
					}}
				>
					<IonLabel slot="label" style={{fontSize: "larger"}}>{loc.name}</IonLabel>
				</IonInput>

				{/* password */}
				<span style={{ display: "flex" }}>
					<IonInput mode="md" className="inputlogin" type={showPwd ? "text" : "password"} required fill="outline" labelPlacement="stacked"
						value={selUser.pass}
						onIonInput={e => {
							selUser.pass = e.detail.value ?? "";
							setLocError(null);
						}}
					>
						<IonLabel slot="label" style={{fontSize: "larger"}}>{loc.password}</IonLabel>
					</IonInput>
					<IonButtons slot="end" style={{ alignSelf: "flex-end", marginBlock: "auto" }}>
						<IonButton onClick={()=>{setShowPwd(v => !v)}}>
							<IonIcon icon={showPwd ? eyeOutline : eyeOffOutline} />
						</IonButton>
					</IonButtons>
				</span>
			</div>


			{/* login button */}
			<IonItem lines="full">
				<IonButton type="submit" style={{width: "100%", height: "3em", marginInline: 0, marginTop: "1em", "--border-radius": "6px"}} disabled={loginWait}>
					{loc.login}
				</IonButton>
			</IonItem>
    </form>

    <div style={{marginTop: "2em"}}>
      <ExternalLink
        text={loc.createAccount}
        href="https://mqtt.bmr.cz/account-register.php"
        setExternalSite={setExternalSite}
      />
      <ExternalLink
        text={loc.resetPassword}
        href="https://mqtt.bmr.cz/password-reset.php"
        setExternalSite={setExternalSite}
      />
      <ExternalLink
        text={loc.deleteAccount}
        href="https://mqtt.bmr.cz/account-unregister.php"
        setExternalSite={setExternalSite}
      />
    </div>

    <IonToast
      isOpen={errorMessage !== null}
      duration={5000}
      onWillDismiss={() => setErrorMessage(null)}
      message={errorMessage ?? ""}
      position="top"
      buttons={[{
        text: "OK",
        role: "cancel"
      }]}
    />

    </IonCard>
    </div>
  </Page>
}


function FindProfileByName(profiles: Profile[], name: string) {
	for (let i = 0; i < name.length; i++) {
		if (name[i] !== '@') continue;
		const user = name.slice(0, i);
		const broker = name.slice(i+1);

		let prof = profiles.findIndex(p => p.name === user && p.broker === broker);
		if (prof > 0) return prof;
	}

	return profiles.findIndex(p => p.name === name);
}

async function SaveProfiles(profiles: Profile[], selId: number, selProfile: Profile | null, destructive: boolean) {
	if (selId === PROF_TMP) return;
	if (!destructive) profiles = [...profiles];

	if (selId === PROF_NEW) selId = profiles.length;

	if (selProfile) {
		profiles[selId] = selProfile;

		// deduplikace
		profiles =
			profiles
			.filter((p, i) =>
				i === selId
				|| p.name   !== selProfile.name
				|| p.broker !== selProfile.broker
			);
	}

	if (selProfile && getPlatforms().includes('ios')) {
		SavePassword.promptDialog({
			username: selProfile.name,
			password: selProfile.pass,
		});
	}

	await SavePreference("users", StringifyUsers(profiles));
	if (selProfile) await SavePreference("selUser", selProfile.name + "@" + selProfile.broker);
	else            await SavePreference("selUser", null);
}

const ExternalLink : FC<{
  href : string,
  text : string,
  setExternalSite : (href : string) => void
}> = (props) =>
<IonItem lines='none' style={{height: "2em"}}>
  <small>
    <a
      href={props.href}
      onClick={ev => {
        ev.preventDefault();
        props.setExternalSite(props.href);
      }}
    >{props.text}</a>
  </small>
</IonItem>


const glob = Glob({
	en: {
		name: "User name",
		password: "Password",
		login: "Log in",
		title: "Power Grid Monitor",

		resetPassword: "Forgotten password",
		deleteAccount: "Delete account",
		createAccount: "Create account",

		connectionFailed: "Could not connect to the MQTT broker.",
		loginFailed: "Incorrect username or password.",

		returnToLogin: "Return to login",
		openInBrowser: "Open in browser",

		rememberName: "Save user name",
		rememberPassword: "Save password",
		forgetrn: "Do you wish to forget this user immediatelly?",
		yes: "Yes",
		no: "No",
		newUser: "New user...",
		selUser: "User",

		profile_new: "New user...",
		profile_tmp: "Login without saving...",
	},
	cs: {
		name: "Jméno",
		password: "Heslo",
		login: "Přihlásit",
		title: "Power Grid Monitor",

		resetPassword: "Zapomenuté heslo",
		deleteAccount: "Smazat účet",
		createAccount: "Vytvořit účet",

		connectionFailed: "Nelze se připojit k serveru.",
		loginFailed: "Nesprávné přihlašovací údaje.",

		returnToLogin: "Zpět na přihlášení",
		openInBrowser: "Otevřít mimo aplikaci",

		rememberName: "Uložit jméno",
		rememberPassword: "Uložit heslo",
		forgetrn: "Chcete uživatele okamžitě zapomenout?",
		yes: "Ano",
		no: "Ne",
		newUser: "Nový uživatel...",
		selUser: "Uživatel",

		profile_new: "Nový uživatel...",
		profile_tmp: "Přihlášení bez uložení...",
	},
	pg: {
		name: "Nome",
		password: "Senha",
		login: "Fazer login",
		title: "Power Grid Monitor",

		resetPassword: "Redefinir senha",
		deleteAccount: "Excluir conta",
		createAccount: "Criar conta",

		connectionFailed: "Não é possível se conectar ao servidor.",
		loginFailed: "Dados de login incorretos.",

		returnToLogin: "Voltar ao login",
		openInBrowser: "Abrir no navegador da web",

		rememberName: "Lembrar o nome",
		rememberPassword: "Lembrar a senha",
		forgetrn: "Deseja esquecer esse usuário imediatamente?",
		yes: "Sim",
		no: "Não",
		newUser: "Novo usuário",
		selUser: "Usuário",

		profile_new: "Novo usuário...",
		profile_tmp: "Fazer login sem salvar...",
	},
	es: {
		name: "Nombre",
		password: "Contraseña",
		login: "Iniciar sesión",
		title: "Power Grid Monitor",

		resetPassword: "Restablecer contraseña",
		deleteAccount: "Borrar cuenta",
		createAccount: "Crear cuenta",

		connectionFailed: "Conexión fallida",
		loginFailed: "Login fallido",

		returnToLogin: "Volver al login",
		openInBrowser: "Abrir en el navegador web",

		rememberName: "Recordar nombre",
		rememberPassword: "Recordar contraseña",
		forgetrn: "¿Olvidar al usuario inmediatamente?",
		yes: "Sí",
		no: "No",
		newUser: "Nuevo usuario",
		selUser: "Usuario",

		profile_new: "Nuevo usuario...",
		profile_tmp: "Iniciar sesión sin guardar...",
	},
} as const);

